import React from "react";
import { Card, CardContent, Typography, Button, Box } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import SecurityIcon from "@mui/icons-material/Security";
import { uploadLink } from "../../utils/constants";
import { getWhatsAppUrl } from "../../utils/whatsappHelper";
import { Product } from "../manage-products/manageProducts";
import { StoreThemeTokens, getStoreTheme } from "./themes/storeThemes";
import { AmazonIcon } from "../../utils/amazonHelper";

interface StoreProductCardProps {
  product: Product;
  onOrder: (product: Product) => void;
  storePhone?: string;
  storeName?: string;
  themeTokens?: StoreThemeTokens;
}

const StoreProductCard: React.FC<StoreProductCardProps> = ({
  product,
  onOrder,
  storePhone,
  storeName,
  themeTokens: propTokens,
}) => {
  const themeTokens = propTokens || getStoreTheme();

  // Image resolution
  const firstImage = product.images && product.images.length > 0 ? product.images[0] : "";
  const imageUrl = firstImage
    ? firstImage.startsWith("http")
      ? firstImage
      : `${uploadLink}${firstImage}`
    : "https://via.placeholder.com/400x400?text=Product";

  // Calculate simulated MRP & discount if not provided
  const price = product.price || 0;
  const originalPrice = Math.round(price * 1.6);
  const discountPercent = Math.round(((originalPrice - price) / originalPrice) * 100);

  // Badge label (Best Seller / Trending / New Arrival)
  const badgeLabels = ["Best Seller", "New Arrival", "Trending", "Popular"];
  const badgeIndex = (product.name.length + (product._id ? product._id.charCodeAt(0) : 0)) % badgeLabels.length;
  const badgeText = badgeLabels[badgeIndex];

  const isInStock = product.inStock !== false && (product as any).in_stock !== false;
  const amazonUrl = (product.amazonLink || product.amazon_link || "").trim();

  const handleWhatsAppChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isInStock) return;
    const phone = storePhone || "";
    if (phone) {
      const msg = `Hi ${storeName || "RUDRIX"}, I want to order "${product.name}" (₹${product.price}).`;
      const waUrl = getWhatsAppUrl(phone, msg);
      if (waUrl) window.open(waUrl, "_blank");
    }
  };

  const handleOrderClick = () => {
    if (!isInStock) return;
    onOrder({
      ...product,
      amazonLink: amazonUrl,
      amazon_link: amazonUrl,
    });
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${themeTokens.cardBorder}`,
        boxShadow: "0 4px 20px -2px rgba(45, 40, 37, 0.05)",
        bgcolor: themeTokens.cardBg,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 16px 32px -4px ${themeTokens.accent}25`,
          borderColor: themeTokens.accent,
        },
      }}
    >
      {/* Product Image Container */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingTop: "100%", // 1:1 Aspect Ratio
          bgcolor: themeTokens.bgSecondary,
          overflow: "hidden",
        }}
      >
        {/* Top-left Badge */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 2,
            bgcolor: isInStock ? themeTokens.badgeBg : "#dc2626",
            color: isInStock ? themeTokens.badgeText : "#ffffff",
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            px: 1.5,
            py: 0.5,
            borderRadius: 1.5,
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
          }}
        >
          {isInStock ? badgeText : "Out of Stock"}
        </Box>

        <img
          src={imageUrl}
          alt={product.name}
          crossOrigin="anonymous"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: isInStock ? "none" : "grayscale(70%) opacity(0.75)",
            transition: "transform 0.5s ease",
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src =
              "https://via.placeholder.com/400x400?text=No+Image";
          }}
        />

        {!isInStock && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(15, 23, 42, 0.4)",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(1.5px)",
            }}
          >
            <Box
              sx={{
                bgcolor: "rgba(220, 38, 38, 0.95)",
                color: "#ffffff",
                px: 2,
                py: 0.75,
                borderRadius: 2,
                fontWeight: 800,
                fontSize: "0.82rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
              }}
            >
              Out of Stock
            </Box>
          </Box>
        )}
      </Box>

      {/* Card Body */}
      <CardContent
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          flex: 1,
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: themeTokens.textPrimary,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={product.name}
          >
            {product.name}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: themeTokens.textSecondary,
              fontSize: "0.8rem",
              lineHeight: 1.4,
              mt: 0.75,
              minHeight: 38,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {product.description || "Premium quality personal care essential with direct guarantee."}
          </Typography>

          {Boolean(product.hasWarranty || product.has_warranty) && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#eff6ff",
                color: "#1d4ed8",
                border: "1px solid #dbeafe",
                px: 1,
                py: 0.35,
                borderRadius: 1.5,
                fontSize: "0.72rem",
                fontWeight: 700,
                mt: 1.25,
                width: "fit-content",
              }}
            >
              <SecurityIcon sx={{ fontSize: 13, color: "#2563eb" }} />
              {product.warrantyPeriod || product.warranty_period || "Warranty Included"}
            </Box>
          )}

          {/* Pricing Row */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, mt: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: themeTokens.textPrimary,
                fontSize: "1.25rem",
              }}
            >
              ₹{price.toLocaleString()}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: themeTokens.textSecondary,
                textDecoration: "line-through",
                fontSize: "0.8rem",
              }}
            >
              ₹{originalPrice.toLocaleString()}
            </Typography>

            <Box
              sx={{
                bgcolor: "#ecfdf5",
                color: "#059669",
                fontSize: "0.68rem",
                fontWeight: 700,
                px: 1,
                py: 0.25,
                borderRadius: 1,
              }}
            >
              {discountPercent}% OFF
            </Box>
          </Box>
        </Box>

        {/* Dual Action Buttons */}
        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${themeTokens.cardBorder}`, display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            disabled={!isInStock}
            onClick={isInStock ? handleOrderClick : undefined}
            startIcon={
              isInStock ? (
                amazonUrl ? (
                  <AmazonIcon size={18} color="#111827" />
                ) : (
                  <ShoppingBagOutlinedIcon sx={{ fontSize: 18 }} />
                )
              ) : undefined
            }
            sx={{
              bgcolor: !isInStock
                ? "#e2e8f0 !important"
                : amazonUrl
                ? "#FF9900"
                : themeTokens.accent,
              color: !isInStock
                ? "#94a3b8 !important"
                : amazonUrl
                ? "#111827"
                : "#FFFFFF",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: 2.5,
              py: 1,
              cursor: !isInStock ? "not-allowed" : "pointer",
              pointerEvents: !isInStock ? "none" : "auto",
              boxShadow: !isInStock
                ? "none"
                : amazonUrl
                ? "0 2px 8px rgba(255, 153, 0, 0.25)"
                : "none",
              "&:hover": {
                bgcolor: amazonUrl ? "#e68a00" : themeTokens.accentHover,
                boxShadow: amazonUrl
                  ? "0 4px 12px rgba(255, 153, 0, 0.35)"
                  : `0 4px 12px ${themeTokens.accent}33`,
              },
            }}
          >
            {!isInStock
              ? "Out of Stock"
              : amazonUrl
              ? "Order Now on Amazon"
              : "Order Now"}
          </Button>

          {storePhone && (
            <Button
              variant="outlined"
              fullWidth
              disabled={!isInStock}
              onClick={isInStock ? handleWhatsAppChat : undefined}
              startIcon={
                <WhatsAppIcon
                  sx={{
                    fontSize: 18,
                    color: !isInStock ? "#94a3b8" : "#16a34a",
                  }}
                />
              }
              sx={{
                borderColor: !isInStock ? "#e2e8f0 !important" : "#bbf7d0",
                bgcolor: !isInStock ? "#f8fafc !important" : "#f0fdf4",
                color: !isInStock ? "#94a3b8 !important" : "#16a34a",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.82rem",
                borderRadius: 2.5,
                py: 0.9,
                cursor: !isInStock ? "not-allowed" : "pointer",
                pointerEvents: !isInStock ? "none" : "auto",
                "&:hover": {
                  bgcolor: !isInStock ? "#f8fafc" : "#dcfce7",
                  borderColor: !isInStock ? "#e2e8f0" : "#86efac",
                },
              }}
            >
              {!isInStock ? "Unavailable" : "Chat on WhatsApp"}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default StoreProductCard;
